import React, { FC, useCallback, useEffect, useState } from 'react'

import { YouTubePlayer } from 'shared/components'
import { transformGenre } from 'shared/pipes'
import { sleep } from 'shared/utils/sleep'

import bg1 from 'assets/_images/16+_bg.jpg'
import logo1 from 'assets/_images/16+_logo.png'
import bg2 from 'assets/_images/kosa_bg.jpg'
import logo2 from 'assets/_images/kosa_logo.png'
import bg3 from 'assets/_images/vertinsky_bg.jpg'
import logo3 from 'assets/_images/vertinsky_logo.png'

import classNames from 'classnames'
import classes from './CatalogItems.module.scss'
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import { useIsMounted, useWindowSize } from 'shared/hooks'

export type CatalogItem = {
  isActive?: boolean
  video: string
  title: string
  titleImg?: string
  bgImg?: string
  year: string
  genre: number[]
  age: string
  plot?: string
  muted?: boolean
  toggleMute?: () => void
  selectNextSlide?: () => void
}
export const RenderCatalogItem: FC<CatalogItem> = ({
  isActive,
  title,
  titleImg,
  bgImg,
  year,
  genre,
  age,
  video,
  plot,
  muted = true,
  toggleMute = () => null,
  selectNextSlide = () => null,
}: CatalogItem) => {
  const [fullscreen, setFullscreen] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [scrollAtTop, setScrollAtTop] = useState(true)
  const [ticking, setTicking] = useState(false)
  const { isMobile, isTablet } = useWindowSize()
  const mobileDevices = isMobile || isTablet

  const isMounted = useIsMounted()

  useEffect(() => {
    if (!mobileDevices) {
      if (!playing && isActive && scrollAtTop) {
        sleep(3000).then(() => {
          if (!isMounted()) return
          setPlaying(true)
        })
      } else if (playing && (!isActive || !scrollAtTop)) {
        if (!isMounted()) return
        sleep(600).then(() => setPlaying(false))
      }
    } else if (mobileDevices && isActive && !ticking) {
      setTicking(true)
      sleep(10000).then(() => {
        if (!isMounted()) return
        selectNextSlide()
        setTicking(false)
      })
    }
  }, [
    mobileDevices,
    playing,
    scrollAtTop,
    isActive,
    isMounted,
    ticking,
    selectNextSlide,
  ])

  const renderTitle = () => {
    if (titleImg) {
      return <img className={`${classes.title} title`} src={titleImg} alt="" />
    }

    return <h1 className={`${classes.title} title`}>{title}</h1>
  }

  const playFullscreen = () => {
    if (muted) toggleMute()
    setPlaying(true)
    setFullscreen(true)
  }

  const exitFullscreen = useCallback(() => {
    setFullscreen(false)
  }, [])

  useScrollPosition(
    ({ currPos }) => {
      const scrolledAway = currPos.y !== 0
      if (scrolledAway && scrollAtTop) {
        setScrollAtTop(false)
      }
    },
    [scrollAtTop],
    undefined,
    true
  )

  useScrollPosition(
    ({ currPos }) => {
      const atTop = currPos.y === 0
      if (atTop && !scrollAtTop) {
        setScrollAtTop(true)
      }
    },
    [scrollAtTop],
    undefined,
    true
  )

  const youTubePlayerConfig = {
    playerVars: {
      enablejsapi: 1,
      showinfo: 0,
      rel: 0,
      fs: 0,
      disablekb: 0,
      controls: 1,
      autoplay: 0,
      // loop: 1,
      // modestbranding: 0,
    },
  }

  const catalogItemWrapperClassName = classNames(classes.slider, {
    [classes.slider_hidden]: !isActive,
    [classes.hoverable]: !mobileDevices,
  })
  const bgClassName = classNames(classes.bg, {
    [classes.animate_fadeOut]: isActive && playing,
    [classes.animate_fadeIn]: isActive && !playing,
  })
  const iconsClassName = classNames(classes.icons, {
    [classes.animate_fadeOut]: !scrollAtTop && !playing,
    [classes.animate_fadeIn]: playing,
  })
  return (
    <div className={catalogItemWrapperClassName}>
      <div className={bgClassName}>
        <div>
          <img src={bgImg} alt="" />
        </div>
      </div>
      <YouTubePlayer
        className={classes.video}
        style={{ pointerEvents: fullscreen ? 'auto' : 'none' }}
        url={video}
        fullscreen={fullscreen}
        exitFullscreen={exitFullscreen}
        playing={playing}
        volume={muted ? 0 : 1}
        toggleMute={toggleMute}
        onEnded={() => selectNextSlide()}
        config={youTubePlayerConfig}
        isActive={isActive}
        isNarrow
      />
      <div className={classes.shade} />
      <div className={`${classes.slider_wrap} container container-full`}>
        <div className={classes.slider_info}>
          {renderTitle()}
          {!mobileDevices && <div className={classes.plot}>{plot || ' '}</div>}
          <div className={classes.descr}>
            {year} {'\u00B7'} {genre.map(g => transformGenre(g)).join(', ')}{' '}
            {'\u00B7'} {age}
          </div>
        </div>
        {isActive && !mobileDevices && (
          <div className={iconsClassName}>
            <div
              className={classes.iconWrapper}
              onClick={() => playFullscreen()}
            >
              <i aria-hidden className="icon-play" />
            </div>
            <div className={classes.iconWrapper} onClick={() => toggleMute()}>
              <i
                aria-hidden
                className={muted ? 'icon-audio-off' : 'icon-audio-on'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const catalogItems: CatalogItem[] = [
  {
    // video: `https://www.youtube.com/watch?v=7quOKV7Xr8k`,
    // ссылку для первого видео нужно перевести в формат ниже, чтобы избежать ошибки от youtube в консоли
    video: `https://www.youtube.com/embed/7quOKV7Xr8k`,
    title: 'Шестнадцать+',
    titleImg: logo1,
    bgImg: bg1,
    year: '2021',
    genre: [6, 15],
    age: '12+',
    plot: 'Десять новелл о поколении ТикТока и Ютуба, вдохновлённые текстами популярных треков. В честных сюжетах, будто подсмотренных в реальности, подростки переживают то, о чём не расскажешь родителям. Любовь и насилие, близость и одиночество, взросление и сексуальность. Герои этих историй — живые люди, мы с вами, наши друзья и дети. А музыка, которую они слушают — ключ к замкнутому миру молодёжной культуры. Под песни АИГЕЛ, Zoloto, Sirotkin, Дани Милохина и других исполнителей пацаны и девчонки нарушают табу, делятся секретами и просто живут свою обычную и в то же время особенную жизнь.',
  },
  {
    video: `https://www.youtube.com/embed/-fTgqnJXiWQ`,
    title: 'Коса',
    titleImg: logo2,
    bgImg: bg2,
    year: '2020',
    genre: [17, 10],
    age: '18+',
    plot: 'Завораживающий триллер в лучших традициях европейского детектива, где пульс любви бьётся в унисон смерти. Полузабытая история о маньяке оживила равнодушный пейзаж Куршской косы: он губит молодость, срезая девочек как цветы, и шлёт приветы старому знакомому – местному журналисту. На запах белых лилий вместе с репортёром идёт следователь Ева Кайдас, со своим букетом тоски и отчаяния. Ниточка за ниточкой она распутывает клубок тонкой философии маньяка, то и дело спотыкаясь о гнетущее прошлое и обжигающее влечение к непрошеному напарнику.',
  },
  {
    video: `https://www.youtube.com/embed/Vf4n80lvSYc`,
    title: 'Вертинский',
    titleImg: logo3,
    bgImg: bg3,
    year: '2022',
    genre: [11, 6],
    age: '18+',
    plot: 'Биографическая драма о жизни и творчестве Александра Вертинского – легендарного артиста российской эстрады XX века. Судьба русского Пьеро тесно переплетена с фундаментальными переменами в отечественной истории: под аккомпанемент Революции, Гражданской войны, белой эмиграции и Второй мировой он прошел путь от бедствующего декадента до кумира миллионов, мечтающего вернуться на Родину. История равномерно рассказывает о всех гранях одной жизни в искусстве: 8 эпизодов, 8 женщин, 8 вечных шлягеров.',
  },
]
